import { type IConsumer, type IEvent, createConsumer, createSubscriber } from '@cian/events-log';
import { type IMicrofrontendManifest } from '@cian/mf-registry/shared';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useListingAppContext } from '@/microfrontends/listing/shared/context/ListingAppContext';

import { useGetModerationComplainMicrofrontendQuery } from '@/packages/query-state/shared/store/moderation-complain-frontend/v1/get-moderation-complain-microfrontend/query';
import { useReduxSelector } from '@/packages/redux-state/store';
import { selectComplaintOfferId } from '@/packages/redux-state/store/slices/complaints/selectors/selectComplaintOfferId';

type UseModerationComplaintResponse = {
  isFetching: boolean;
  refetch(): void;
  status: 'error' | 'success' | 'loading';
  offerId: number;
  manifest?: IMicrofrontendManifest;
};

const MICROFRONTEND_NAME = 'moderation-complain-frontend';
let consumer: IConsumer;

export const useModerationComplaint = (onClose: () => void): UseModerationComplaintResponse | null => {
  const { microfrontendsRegistry } = useListingAppContext();
  const offerId = useReduxSelector(selectComplaintOfferId);

  const { data, status, refetch, isFetching } = useGetModerationComplainMicrofrontendQuery({});
  const registryMicrofront = !!microfrontendsRegistry.microfrontend(MICROFRONTEND_NAME);
  const [manifest, setManifest] = useState<IMicrofrontendManifest | undefined>(data);

  const effectHandler = useCallback(() => {
    consumer ??= createConsumer({ topic: 'complaintsModal' });

    const complaintsSubscriber = createSubscriber((event: IEvent<unknown>) => {
      if (event.type === 'popup_close') {
        consumer.unsubscribe(complaintsSubscriber);
        onClose();
      }
    });

    consumer.subscribe(complaintsSubscriber);

    return (): void => {
      consumer.unsubscribe(complaintsSubscriber);
    };
  }, [onClose]);

  useEffect(() => {
    if (data && !registryMicrofront) {
      microfrontendsRegistry.register(data);
      setManifest(data);
    }
  }, [data, registryMicrofront, microfrontendsRegistry, setManifest]);

  useEffect(() => {
    const clear = effectHandler();

    return clear;
  }, [effectHandler]);

  return useMemo(
    () =>
      offerId
        ? {
            isFetching,
            manifest,
            offerId,
            refetch,
            status,
          }
        : null,
    [isFetching, manifest, offerId, refetch, status],
  );
};
